import {
  GaiaEventType,
  WebsocketSubscriptionTarget,
  type GaiaEvent,
  type GaiaEventObjectPayload,
} from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { logger } from '@/utils/logger';

export interface UseVaultWebSocketOptions {
  /**
   * Directory uids to watch: the current directory plus every parent in the
   * breadcrumb chain. Reconciled live — subscriptions follow this set. Omit to
   * watch files only (e.g. a single-file preview).
   */
  directoryUids?: () => string[];
  /** The uid of the directory currently being viewed (distinguishes "current" from "parent"). */
  currentDirectoryUid?: () => string | null;
  /**
   * File uids to watch. Populated only while watching files (e.g. a search
   * result set or a single previewed file); omit when watching directories only.
   */
  fileUids?: () => string[];

  /** A `vault_directory_updated` arrived for the current directory — refetch its contents. */
  onCurrentDirectoryUpdated?: () => void;
  /** A `vault_directory_updated` arrived for a parent directory — update its breadcrumb label. */
  onParentDirectoryUpdated?: (uid: string, name: string) => void;
  /** A `vault_directory_deleted` arrived for the current directory or a parent — go home. */
  onCurrentOrParentDeleted?: (uid: string) => void;
  /** A `vault_file_updated` arrived for a watched file — replace it with the carried data. */
  onFileUpdated?: (uid: string, fileData: Record<string, unknown>) => void;
  /** A `vault_file_deleted` arrived for a watched file — remove it. */
  onFileDeleted?: (uid: string) => void;
  /**
   * A `contract_management_model_extraction_updated` arrived for a watched file —
   * a built-in extraction (`task_type`) moved to `status` running / done / failed.
   */
  onFileModelExtractionUpdated?: (uid: string, data: Record<string, unknown>) => void;
}

function asObjectPayload(data: GaiaEvent['data']): GaiaEventObjectPayload | null {
  return typeof data === 'object' && 'uid' in data && 'data' in data ? data : null;
}

function payloadName(payload: GaiaEventObjectPayload): string {
  const name = payload.data.name;
  return typeof name === 'string' ? name : '';
}

/**
 * Keep a vault view (directory listing + breadcrumb chain, or a watched set of
 * files) in sync with the backend's vault websocket events.
 *
 * The single shared socket fans every subscribed event out to every mounted
 * vault view, so each caller filters incoming events against its own
 * `directoryUids` / `fileUids` before acting.
 */
export function useVaultWebSocket(options: UseVaultWebSocketOptions): void {
  const disposers: (() => void)[] = [];
  const dirSubs = new Map<string, () => void>();
  const fileSubs = new Map<string, () => void>();

  function reconcile(
    target: WebsocketSubscriptionTarget,
    desired: string[],
    subs: Map<string, () => void>
  ): void {
    const wsClient = getWsClient();
    const desiredSet = new Set(desired.filter(Boolean));

    for (const [uid, dispose] of subs) {
      if (!desiredSet.has(uid)) {
        dispose();
        subs.delete(uid);
      }
    }
    for (const uid of desiredSet) {
      if (!subs.has(uid)) {
        subs.set(uid, wsClient.addSubscriptionUid(target, uid));
      }
    }
  }

  const handler: WebSocketEventHandler = event => {
    const payload = asObjectPayload(event.data);
    if (!payload) {
      return;
    }
    const uid = payload.uid;

    switch (event.gaia_event_type) {
      case GaiaEventType.VAULT_DIRECTORY_UPDATED: {
        if (uid === options.currentDirectoryUid?.()) {
          options.onCurrentDirectoryUpdated?.();
        } else if (options.directoryUids?.().includes(uid)) {
          options.onParentDirectoryUpdated?.(uid, payloadName(payload));
        }
        break;
      }
      case GaiaEventType.VAULT_DIRECTORY_DELETED: {
        if (uid === options.currentDirectoryUid?.() || options.directoryUids?.().includes(uid)) {
          options.onCurrentOrParentDeleted?.(uid);
        }
        break;
      }
      case GaiaEventType.VAULT_FILE_UPDATED: {
        if (options.fileUids?.().includes(uid)) {
          options.onFileUpdated?.(uid, payload.data);
        }
        break;
      }
      case GaiaEventType.VAULT_FILE_DELETED: {
        if (options.fileUids?.().includes(uid)) {
          options.onFileDeleted?.(uid);
        }
        break;
      }
      case GaiaEventType.CONTRACT_MANAGEMENT_MODEL_EXTRACTION_UPDATED: {
        if (options.fileUids?.().includes(uid)) {
          options.onFileModelExtractionUpdated?.(uid, payload.data);
        }
        break;
      }
      default:
        break;
    }
  };

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }

    const wsClient = getWsClient();
    const directoryUids = options.directoryUids;
    if (directoryUids) {
      disposers.push(
        wsClient.registerHandler(WebsocketSubscriptionTarget.VAULT_DIRECTORY, handler),
        watch(
          directoryUids,
          uids => {
            reconcile(WebsocketSubscriptionTarget.VAULT_DIRECTORY, uids, dirSubs);
          },
          { immediate: true }
        )
      );
    }
    const fileUids = options.fileUids;
    if (fileUids) {
      disposers.push(
        wsClient.registerHandler(WebsocketSubscriptionTarget.VAULT_FILE, handler),
        watch(
          fileUids,
          uids => {
            reconcile(WebsocketSubscriptionTarget.VAULT_FILE, uids, fileSubs);
          },
          {
            immediate: true,
          }
        )
      );
    }
  });

  onBeforeUnmount(() => {
    for (const dispose of dirSubs.values()) {
      dispose();
    }
    dirSubs.clear();
    for (const dispose of fileSubs.values()) {
      dispose();
    }
    fileSubs.clear();
    for (const dispose of disposers) {
      dispose();
    }
    disposers.length = 0;
  });
}
